import { getAccessToken } from "@/utils/tokenManager";
import {
  ANALYTICS_ENABLED,
  BATCH_FLUSH_MS,
  BATCH_HARD_LIMIT,
  BATCH_MAX_EVENTS,
  TRACK_ENDPOINT,
} from "./config";
import { getAnonymousId, getBrowser, getDeviceType, getOs } from "./identity";
import { resolveSession } from "./session";
import { getSource } from "./source";
import { getActiveUtm } from "./utm";
import type {
  ClientEventName,
  EventProperties,
  TrackEvent,
  TrackOverrides,
} from "./types";
import { uuid } from "./uuid";

let enabled = ANALYTICS_ENABLED;
let queue: TrackEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let sending = false;

const cleanProps = (props?: EventProperties): EventProperties | undefined => {
  if (!props) return undefined;
  const out: EventProperties = {};
  for (const [key, value] of Object.entries(props)) {
    if (value !== undefined && value !== null) out[key] = value;
  }
  return Object.keys(out).length ? out : undefined;
};

const buildEvent = (
  eventName: ClientEventName,
  properties: EventProperties | undefined,
  overrides: TrackOverrides | undefined,
  sessionId: string,
): TrackEvent => {
  const event: TrackEvent = {
    event_id: overrides?.event_id ?? uuid(),
    event_name: eventName,
    event_time: new Date().toISOString(),
    session_id: sessionId,
    anonymous_id: getAnonymousId(),
    source: getSource(),
    device_type: getDeviceType(),
    ...getActiveUtm(),
  };

  if (typeof window !== "undefined") {
    event.page_url = window.location.pathname + window.location.search;
  }
  if (typeof document !== "undefined" && document.referrer) {
    event.referrer = document.referrer;
  }

  if (overrides?.mini_app_uuid) event.mini_app_uuid = overrides.mini_app_uuid;
  if (typeof overrides?.course_id === "number") event.course_id = overrides.course_id;
  if (typeof overrides?.lesson_id === "number") event.lesson_id = overrides.lesson_id;

  const props = cleanProps(properties);
  if (props) event.properties = props;

  return event;
};

const sessionStartedProperties = (): EventProperties => ({
  landing_page:
    typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : undefined,
  os: getOs(),
  browser: getBrowser(),
});

const scheduleFlush = () => {
  if (flushTimer || !queue.length) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, BATCH_FLUSH_MS);
};

/**
 * Queue an event. Auto-attaches the envelope, ensures a session exists, and
 * emits `session_started` the first time a new session appears. Fire-and-forget
 * — it never throws and never blocks the UI.
 */
export const enqueue = (
  eventName: ClientEventName,
  properties?: EventProperties,
  overrides?: TrackOverrides,
): void => {
  if (!enabled) return;

  const { id, isNew } = resolveSession();
  if (isNew && eventName !== "session_started") {
    queue.push(buildEvent("session_started", sessionStartedProperties(), undefined, id));
  }

  if (eventName === "session_started") {
    // Only emit when this call genuinely opened a new session.
    if (isNew) {
      queue.push(buildEvent("session_started", sessionStartedProperties(), overrides, id));
    }
  } else {
    queue.push(buildEvent(eventName, properties, overrides, id));
  }

  if (queue.length >= BATCH_MAX_EVENTS) {
    void flush();
  } else {
    scheduleFlush();
  }
};

const post = async (events: TrackEvent[]): Promise<"ok" | "drop" | "retry"> => {
  const token = getAccessToken();
  try {
    const res = await fetch(TRACK_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ events }),
      keepalive: true,
    });

    if (res.status === 404) {
      // Analytics feature is disabled server-side — stop emitting this session.
      enabled = false;
      return "drop";
    }
    if (res.status === 422) {
      // Invalid payload: dropping avoids a poison-pill retry loop.
      if (import.meta.env.DEV) {
        console.warn("[analytics] /track rejected the whole batch (422)", events);
      }
      return "drop";
    }

    // A 202 can still carry rejected events — each one is validated on its own.
    // Nobody reads 202 bodies until something is already wrong, and this is how
    // a slug typo or a missing required field surfaces before it costs a week of
    // data.
    if (res.ok && import.meta.env.DEV) {
      void res
        .clone()
        .json()
        .then((body) => {
          const rejected = body?.data?.rejected ?? 0;
          if (rejected > 0) {
            console.warn(`[analytics] /track rejected ${rejected} event(s)`, body?.data?.errors);
          }
        })
        .catch(() => {
          /* a non-JSON 202 tells us nothing — not worth surfacing */
        });
    }

    return res.ok ? "ok" : "retry";
  } catch {
    return "retry";
  }
};

/** Flush queued events to `/track`. Requeues on transient failure for retry. */
export const flush = async (): Promise<void> => {
  if (sending || !queue.length || !enabled) return;

  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  sending = true;
  const batch = queue.slice(0, BATCH_HARD_LIMIT);
  queue = queue.slice(BATCH_HARD_LIMIT);

  try {
    const result = await post(batch);
    if (result === "retry") {
      // Same event_id is reused, so a redelivery is de-duped server-side.
      queue = [...batch, ...queue].slice(0, BATCH_HARD_LIMIT * 4);
    }
  } finally {
    sending = false;
    if (queue.length) scheduleFlush();
  }
};

/**
 * Synchronous best-effort flush for page unload. Uses `sendBeacon` so the
 * request survives the page going away (no auth header is possible, but the
 * anonymous_id in the body still lets the backend stitch the session).
 */
export const flushBeacon = (): void => {
  if (!enabled || !queue.length) return;
  if (typeof navigator === "undefined" || !navigator.sendBeacon) {
    void flush();
    return;
  }

  const batch = queue.slice(0, BATCH_HARD_LIMIT);
  queue = queue.slice(BATCH_HARD_LIMIT);
  try {
    const blob = new Blob([JSON.stringify({ events: batch })], {
      type: "application/json",
    });
    const ok = navigator.sendBeacon(TRACK_ENDPOINT, blob);
    if (!ok) queue = [...batch, ...queue];
  } catch {
    queue = [...batch, ...queue];
  }
};

/** Test/escape hatch: current queue depth. */
export const __queueLength = () => queue.length;
